import type { ExcalidrawElement } from "@excalidraw/excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  ExcalidrawImperativeAPI,
  NormalizedZoomValue,
} from "@excalidraw/excalidraw/types";
import { jwtDecode } from "jwt-decode";
import { ArrowLeft, Download, Eraser, Pen, Play, Timer, Undo } from "lucide-react";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Canvas as CanvasType, ViewportMetadata } from "../../../shared/types";
import { Header } from "../components/Header";
import { useToast } from "../contexts/ToastContext";
import { useDebounce } from "../hooks/useDebounce";
import { useWindowSize } from "../hooks/useWindowSize";
import { getCanvas, nextTurn, saveCanvas, setSketchingStatus } from "../lib/api";
import { calculateSceneCenter, calculateScrollForCenter } from "../lib/view";

const Excalidraw = lazy(() =>
  import("@excalidraw/excalidraw").then((module) => {
    if (typeof window !== "undefined") {
      // @ts-expect-error
      window.EXCALIDRAW_ASSET_PATH = "/";
    }
    return {
      default: module.Excalidraw,
    };
  }),
);

interface CanvasProps {
  canvasId: number;
  onBack: () => void;
  token: string;
  onTurnEnd: (updatedCanvas: CanvasType) => void;
}

const TIMER_DURATION = 180; // 180 seconds

const COLORS = [
  { name: "black", value: "#000000" },
  { name: "red", value: "#e03131" },
  { name: "pink", value: "#f06595" },
  { name: "grape", value: "#ae3ec9" },
  { name: "violet", value: "#7048e8" },
  { name: "blue", value: "#1971c2" },
  { name: "cyan", value: "#0c8599" },
  { name: "teal", value: "#099268" },
  { name: "green", value: "#2f9e41" },
  { name: "lime", value: "#74b816" },
  { name: "yellow", value: "#f08c00" },
  { name: "orange", value: "#e67700" },
];

const SIZES = [
  { name: "small", value: 1, dotSize: 4 },
  { name: "medium", value: 2, dotSize: 7 },
  { name: "large", value: 4, dotSize: 10 },
];

function TurnTimer({
  isTurnActive,
  isMyTurn,
  onEndTurn,
  iconSize,
}: {
  isTurnActive: boolean;
  isMyTurn: boolean;
  onEndTurn: () => void;
  iconSize: number;
}) {
  const [timeLeft, setTimeLeft] = useState(TIMER_DURATION);

  useEffect(() => {
    if (isTurnActive) {
      setTimeLeft(TIMER_DURATION);
    }
  }, [isTurnActive]);

  useEffect(() => {
    let timer: number | undefined;
    if (isTurnActive && timeLeft > 0) {
      timer = window.setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    } else if (isTurnActive && timeLeft === 0) {
      onEndTurn();
    }
    return () => clearInterval(timer);
  }, [isTurnActive, timeLeft, onEndTurn]);

  if (!isMyTurn) {
    return (
      <div className="timer-box" style={{ borderRadius: "66px 61px 81px 84px / 84px 64px 51px 81px" }}>
        not your turn!
      </div>
    );
  }

  if (isTurnActive) {
    return (
      <div className="timer-text flex-gap-4">
        <Timer size={iconSize} />
        {timeLeft}s
      </div>
    );
  }

  return null;
}

export function Canvas({ canvasId, onBack, token, onTurnEnd }: CanvasProps) {
  const initialElementIdsRef = useRef<Set<string>>(new Set());
  const [appState, setAppState] = useState<Partial<AppState>>({
    currentItemStrokeColor: "#000000",
    currentItemStrokeWidth: 1,
    activeTool: {
      type: "freedraw",
      lastActiveTool: null,
      locked: false,
      customType: null,
    } as AppState["activeTool"],
  });
  const [initialData, setInitialData] = useState<{
    elements: readonly ExcalidrawElement[];
    appState: Partial<AppState>;
    files?: Record<string, BinaryFileData>;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [excalidrawAPI, setExcalidrawAPI] = useState<ExcalidrawImperativeAPI | null>(null);

  const [isTurnActive, setIsTurnActive] = useState(false);
  const [turnInfo, setTurnInfo] = useState<{
    userId: number | null;
    username: string;
    isSketching: boolean;
    collaborators: string[];
  }>({
    userId: null,
    username: "",
    isSketching: false,
    collaborators: [],
  });
  const pendingViewportRef = useRef<ViewportMetadata | null>(null);

  const authorityRef = useRef({
    lastAppliedVersion: "",
    lastSavedData: "",
    isTurnActive: false,
    hasSyncedInitialView: false,
    elements: [] as readonly ExcalidrawElement[],
  });

  useEffect(() => {
    authorityRef.current.isTurnActive = isTurnActive;
  }, [isTurnActive]);

  const { isMobile } = useWindowSize();
  const appStateRef = useRef(appState);

  useEffect(() => {
    appStateRef.current = appState;
  }, [appState]);

  const iconSize = isMobile ? 18 : 14;
  const markerIconSize = isMobile ? 20 : 16;
  const { addToast } = useToast();

  const userId = useMemo(() => {
    if (!token) return null;
    const decoded: { userId: number } = jwtDecode(token);
    return decoded.userId;
  }, [token]);

  const isMyTurn = useMemo(
    () => userId !== null && Number(userId) === Number(turnInfo.userId),
    [userId, turnInfo.userId],
  );

  // 1. The Reconciler: Single source of truth for sync logic
  const fetchLatest = useCallback(async () => {
    try {
      const data = await getCanvas(canvasId);

      const parsedData = data.data ? JSON.parse(data.data) : null;
      const elements = parsedData?.elements || [];
      const files = parsedData?.files || {};
      const { ...restAppState } = parsedData?.appState || {};

      setTurnInfo({
        userId: data.current_turn_user_id,
        username: (data.current_turn_username || data.owner || "someone").trim(),
        isSketching: data.is_sketching || false,
        collaborators: data.collaborator_usernames ? data.collaborator_usernames.split(", ") : [],
      });
      pendingViewportRef.current = data.last_viewport ? JSON.parse(data.last_viewport) : null;

      if (!initialData) {
        setInitialData({
          elements,
          files,
          appState: {
            activeTool: { type: "freedraw" } as AppState["activeTool"],
          },
        });
        authorityRef.current.lastAppliedVersion = data.last_modified;
        authorityRef.current.lastSavedData = JSON.stringify({
          elements,
          appState: restAppState,
        });
        authorityRef.current.elements = elements;
        return;
      }

      // Authority Check: Only apply drawing updates if server is newer and user isn't drawing
      if (
        !authorityRef.current.isTurnActive &&
        data.last_modified > authorityRef.current.lastAppliedVersion
      ) {
        authorityRef.current.lastAppliedVersion = data.last_modified;
        authorityRef.current.elements = elements;
        authorityRef.current.lastSavedData = JSON.stringify({
          elements,
          appState: restAppState,
        });

        if (excalidrawAPI) {
          if (files) excalidrawAPI.addFiles(Object.values(files));
          excalidrawAPI.updateScene({ elements });
        }
      }
    } catch (error) {
      console.error("Reconciliation failed:", error);
    } finally {
      setIsLoading(false);
    }
  }, [canvasId, initialData, excalidrawAPI]);

  // 2. Event Listeners (SSE & Turn Cleanup)
  useEffect(() => {
    if (!token || isTurnActive) return;
    const es = new EventSource(`/api/events?token=${encodeURIComponent(token)}`);
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === "canvasChanged" || data.type === "turnChanged") fetchLatest();
      } catch {}
    };
    return () => es.close();
  }, [token, isTurnActive, fetchLatest]);

  useEffect(() => {
    fetchLatest();
  }, [fetchLatest]);

  useEffect(() => {
    if (!isTurnActive) fetchLatest();
  }, [isTurnActive, fetchLatest]);

  // 4. Persistence (Surgical Handover)
  const debouncedSave = useDebounce<
    (newElements: readonly ExcalidrawElement[], newAppState: AppState) => void
  >((newElements, newAppState) => {
    if (!isMyTurn || !authorityRef.current.isTurnActive) return;
    const { ...restAppState } = newAppState;
    const dataToSave = {
      elements: newElements as ExcalidrawElement[],
      appState: restAppState,
    };

    const currentZoom = newAppState.zoom?.value ?? 1;
    const { centerX, centerY } = calculateSceneCenter(
      newAppState.width ?? window.innerWidth,
      newAppState.height ?? window.innerHeight,
      newAppState.scrollX ?? 0,
      newAppState.scrollY ?? 0,
      currentZoom,
    );
    const viewportJson = JSON.stringify({
      centerX,
      centerY,
      zoom: currentZoom,
    });

    const serialized = JSON.stringify(dataToSave);
    if (serialized === authorityRef.current.lastSavedData) return;
    authorityRef.current.lastSavedData = serialized;

    saveCanvas(canvasId, dataToSave, viewportJson)
      .then((res) => {
        authorityRef.current.lastAppliedVersion = res.last_modified;
      })
      .catch(console.error);
  }, 1000);

  const handleEndTurn = useCallback(async () => {
    if (!isMyTurn) return;
    addToast("passing the note...", "success");

    try {
      const currentZoom = appStateRef.current?.zoom?.value ?? 1;
      const { centerX, centerY } = calculateSceneCenter(
        appStateRef.current?.width ?? window.innerWidth,
        appStateRef.current?.height ?? window.innerHeight,
        appStateRef.current?.scrollX ?? 0,
        appStateRef.current?.scrollY ?? 0,
        currentZoom,
      );

      const viewportJson = JSON.stringify({
        centerX,
        centerY,
        zoom: currentZoom,
      });

      const { ...restAppState } = appStateRef.current as Partial<AppState>;
      const dataToSave = {
        elements: authorityRef.current.elements as ExcalidrawElement[],
        appState: restAppState,
      };

      const saveResult = await saveCanvas(canvasId, dataToSave, viewportJson);
      authorityRef.current.lastAppliedVersion = saveResult.last_modified;
      authorityRef.current.lastSavedData = JSON.stringify(dataToSave);

      setIsTurnActive(false);

      const updatedCanvasRaw = await nextTurn(canvasId);
      if (updatedCanvasRaw.last_modified)
        authorityRef.current.lastAppliedVersion = updatedCanvasRaw.last_modified;

      onTurnEnd({
        ...updatedCanvasRaw,
        collaborators: JSON.parse(updatedCanvasRaw.collaborators),
      });
    } catch (error) {
      console.error("Handover failed:", error);
      addToast("could not advance turn.", "error");
    }
  }, [canvasId, isMyTurn, onTurnEnd, addToast]);

  const handleStartTurn = useCallback(() => {
    initialElementIdsRef.current = new Set(authorityRef.current.elements.map((el) => el.id));
    setIsTurnActive(true);
    setSketchingStatus(canvasId, true).catch(console.error);

    const startState = {
      currentItemStrokeColor: "#000000",
      currentItemStrokeWidth: 1,
      activeTool: {
        type: "freedraw",
        lastActiveTool: null,
        locked: false,
        customType: null,
      } as AppState["activeTool"],
    };
    setAppState((prev) => ({
      ...prev,
      ...startState,
    }));
    excalidrawAPI?.updateScene({
      appState: startState as AppState,
    });
  }, [excalidrawAPI, canvasId]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (authorityRef.current.isTurnActive && isMyTurn) {
        setSketchingStatus(canvasId, false).catch(() => {});
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (authorityRef.current.isTurnActive && isMyTurn) {
        setSketchingStatus(canvasId, false).catch(() => {});
      }
    };
  }, [canvasId, isMyTurn]);

  const updateScene = useCallback(
    (state: Partial<AppState>) => {
      excalidrawAPI?.updateScene({
        appState: state as AppState,
      });
    },
    [excalidrawAPI],
  );

  const handleUndo = useCallback(() => {
    if (!excalidrawAPI || !authorityRef.current.isTurnActive) return;
    const currentElements = authorityRef.current.elements;
    const lastElement = currentElements[currentElements.length - 1];

    if (lastElement && !initialElementIdsRef.current.has(lastElement.id)) {
      const nextElements = currentElements.slice(0, -1);
      authorityRef.current.elements = nextElements;
      excalidrawAPI.updateScene({ elements: nextElements });
      debouncedSave(nextElements, appStateRef.current as AppState);
    }
  }, [excalidrawAPI, debouncedSave]);

  const handleExport = useCallback(async () => {
    if (!excalidrawAPI) return;

    // Filter out potentially corrupt elements or outliers
    const elements = excalidrawAPI.getSceneElements().filter((el) => {
      if (!el.x || !el.y || !Number.isFinite(el.x) || !Number.isFinite(el.y)) return false;
      if (Math.abs(el.x) > 100000 || Math.abs(el.y) > 100000) return false;
      return true;
    });

    if (elements.length === 0) {
      addToast("nothing to export!", "info");
      return;
    }

    try {
      const { exportToBlob } = await import("@excalidraw/excalidraw");

      const attemptExport = async (maxDim: number) => {
        return await exportToBlob({
          elements,
          mimeType: "image/png",
          exportPadding: 20,
          maxWidthOrHeight: maxDim,
          appState: {
            exportWithDarkMode: false,
            exportBackground: true,
            viewBackgroundColor: "#ffffff",
          },
          files: excalidrawAPI.getFiles(),
        });
      };

      let blob: Blob | null = null;
      // Try high-res first (4096), then fall back to safer sizes
      const targets = [4096, 2048, 1024];
      let lastError: unknown = null;

      for (const target of targets) {
        try {
          blob = await attemptExport(target);
          if (blob) break;
        } catch (e) {
          console.warn(`Export at ${target} failed, trying next fallback...`, e);
          lastError = e;
        }
      }

      if (!blob) throw lastError || new Error("Failed to generate image blob");

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `note-${canvasId}.png`;
      link.click();
      window.URL.revokeObjectURL(url);
      addToast("exported!", "success");
    } catch (error) {
      console.error("Export failure:", error);
      addToast("export failed.", "error");
    }
  }, [excalidrawAPI, canvasId, addToast]);

  const handleBack = useCallback(() => {
    if (authorityRef.current.isTurnActive && isMyTurn) {
      handleEndTurn();
    }
    onBack();
  }, [isMyTurn, handleEndTurn, onBack]);

  const statusText = useMemo(() => {
    if (isLoading) return "";
    if (isMyTurn) {
      return isTurnActive ? "click back to pass back!" : "your turn!";
    }
    return turnInfo.isSketching
      ? `${turnInfo.username} is sketching...`
      : `waiting for ${turnInfo.username}...`;
  }, [isLoading, isMyTurn, isTurnActive, turnInfo.isSketching, turnInfo.username]);

  const markerTools = useMemo(() => {
    if (!isMyTurn || !isTurnActive) return null;

    return (
      <div className="toolbar-group">
        <button
          type="button"
          onClick={() => excalidrawAPI?.setActiveTool({ type: "freedraw" })}
          className={`toolbar-btn ${appState.activeTool?.type === "freedraw" ? "active" : ""}`}
          style={{ borderRadius: "65px 96px 93px 99px / 62px 59px 64px 74px" }}
          title="Pen"
        >
          <Pen size={markerIconSize} />
        </button>
        <button
          type="button"
          onClick={() => excalidrawAPI?.setActiveTool({ type: "eraser" })}
          className={`toolbar-btn ${appState.activeTool?.type === "eraser" ? "active" : ""}`}
          style={{ borderRadius: "93px 93px 69px 81px / 69px 64px 54px 68px" }}
          title="Eraser"
        >
          <Eraser size={markerIconSize} />
        </button>
      </div>
    );
  }, [isMyTurn, isTurnActive, appState.activeTool?.type, markerIconSize, excalidrawAPI]);

  const undoButton = useMemo(() => {
    if (!isMyTurn || !isTurnActive) return null;

    return (
      <button
        type="button"
        onClick={handleUndo}
        className="toolbar-btn"
        style={{ borderRadius: "66px 69px 99px 69px / 80px 94px 78px 56px" }}
        title="Undo"
      >
        <Undo size={markerIconSize} />
      </button>
    );
  }, [isMyTurn, isTurnActive, markerIconSize, handleUndo]);

  const sizeSelector = useMemo(() => {
    if (!isMyTurn || !isTurnActive || appState.activeTool?.type !== "freedraw") return null;

    return (
      <div className="size-selector">
        {SIZES.map((size) => (
          <button
            key={size.value}
            type="button"
            onClick={() => updateScene({ currentItemStrokeWidth: size.value })}
            className={`size-btn ${appState.currentItemStrokeWidth === size.value ? "active" : ""}`}
            style={{ borderRadius: "68px 54px 85px 51px / 82px 77px 59px 79px" }}
            title={size.name}
          >
            <div
              style={{
                width: `${size.dotSize}px`,
                height: `${size.dotSize}px`,
                backgroundColor: "currentColor",
                borderRadius: "50%",
                display: "block",
                minWidth: `${size.dotSize}px`,
                minHeight: `${size.dotSize}px`,
              }}
            />
          </button>
        ))}
      </div>
    );
  }, [
    isMyTurn,
    isTurnActive,
    appState.activeTool?.type,
    appState.currentItemStrokeWidth,
    updateScene,
  ]);

  const colorSelector = useMemo(() => {
    if (!isMyTurn || !isTurnActive || appState.activeTool?.type !== "freedraw") return null;

    return (
      <div className="color-selector">
        {COLORS.map((color, index) => {
          return (
            <button
              key={color.value}
              type="button"
              onClick={() => updateScene({ currentItemStrokeColor: color.value })}
              className={`color-btn ${appState.currentItemStrokeColor === color.value ? "active" : ""}`}
              style={{ borderRadius: "83px 88px 73px 66px / 95px 95px 81px 87px" }}
              title={color.name}
              data-color={color.value}
            />
          );
        })}
      </div>
    );
  }, [
    isMyTurn,
    isTurnActive,
    appState.activeTool?.type,
    appState.currentItemStrokeColor,
    updateScene,
  ]);

  const turnUI = useMemo(
    () => (
      <div className="turn-ui">
        <TurnTimer
          isTurnActive={isTurnActive}
          isMyTurn={isMyTurn}
          onEndTurn={handleEndTurn}
          iconSize={iconSize}
        />
        {isMyTurn && !isTurnActive && (
          <button
            type="button"
            onClick={handleStartTurn}
            className="btn btn-primary flex-gap-4"
            style={{ borderRadius: "55px 97px 79px 83px / 100px 73px 82px 66px" }}
          >
            <Play size={iconSize} />
            start!
          </button>
        )}
      </div>
    ),
    [isMyTurn, isTurnActive, iconSize, handleEndTurn, handleStartTurn],
  );

  const exportButton = useMemo(
    () => (
      <button
        type="button"
        onClick={handleExport}
        className="btn-icon"
        title="export as image"
        style={{ borderRadius: "97px 58px 58px 78px / 83px 61px 92px 100px" }}
      >
        <Download size={iconSize} />
      </button>
    ),
    [handleExport, iconSize],
  );

  const handleExcalidrawChange = useCallback(
    (newElements: readonly ExcalidrawElement[], newAppState: AppState) => {
      authorityRef.current.elements = newElements;

      // Apply pending viewport — onChange guarantees accurate width/height after layout
      const pendingViewport = pendingViewportRef.current;
      if (pendingViewport && newAppState.width && newAppState.height) {
        const { centerX, centerY, zoom } = pendingViewport;
        pendingViewportRef.current = null;
        const { scrollX, scrollY } = calculateScrollForCenter(
          newAppState.width,
          newAppState.height,
          centerX,
          centerY,
          zoom,
        );
        excalidrawAPI?.updateScene({
          appState: {
            scrollX,
            scrollY,
            zoom: { value: zoom as NormalizedZoomValue },
          },
        });
        return;
      }

      // Disable pen mode so finger pinch-to-zoom works on iPad
      if (newAppState.penMode && excalidrawAPI) {
        excalidrawAPI.updateScene({ appState: { penMode: false } });
      }

      // Only update state for properties that trigger UI changes to avoid heavy re-renders
      if (
        newAppState.activeTool?.type !== appStateRef.current.activeTool?.type ||
        newAppState.currentItemStrokeColor !== appStateRef.current.currentItemStrokeColor ||
        newAppState.currentItemStrokeWidth !== appStateRef.current.currentItemStrokeWidth
      ) {
        setAppState((prev) => ({
          ...prev,
          activeTool: newAppState.activeTool,
          currentItemStrokeColor: newAppState.currentItemStrokeColor,
          currentItemStrokeWidth: newAppState.currentItemStrokeWidth,
        }));
      }

      appStateRef.current = newAppState;
      debouncedSave(newElements, newAppState);
    },
    [debouncedSave, excalidrawAPI],
  );

  const excalidrawUIOptions = useMemo(
    () => ({
      canvasActions: {
        loadScene: false,
        export: false as const,
        saveAsImage: false,
        themeSelection: false,
        clearCanvas: false,
      },
    }),
    [],
  );

  return (
    <div className="canvas-wrapper">
      <Header>
        <div className="header-left">
          <button
            type="button"
            onClick={handleBack}
            className="btn btn-primary btn-padding-x-lg flex-shrink-0"
            style={{ borderRadius: "51px 86px 65px 52px / 99px 61px 58px 87px" }}
          >
            <ArrowLeft size={iconSize} />
          </button>
          <span className="status-text">{statusText}</span>
        </div>
        <div className="header-right">
          {exportButton}
          {turnInfo.collaborators.length > 1 ? turnUI : <div className="spacer-w-68" />}
        </div>
      </Header>

      <div className="canvas-layout">
        <div className="canvas-main">
          <div
            className="canvas-frame"
            onContextMenu={(e) => e.preventDefault()}
            role="application"
            aria-label="drawing canvas"
            tabIndex={-1}
          >
            {isTurnActive && (
              <>
                <aside className="canvas-sidebar canvas-sidebar-left">
                  <div className="sidebar-section">{markerTools}</div>
                  {appState.activeTool?.type === "freedraw" && (
                    <>
                      <div className="sidebar-divider" />
                      <div className="sidebar-section">{sizeSelector}</div>
                    </>
                  )}
                  <div className="sidebar-divider" />
                  <div className="sidebar-section">{undoButton}</div>
                </aside>
                <aside className="canvas-sidebar canvas-sidebar-right">
                  <div className="sidebar-section">{colorSelector}</div>
                </aside>
              </>
            )}
            {!isLoading && initialData && (
              <Suspense fallback={<div className="flex-center h-full">Loading...</div>}>
                <Excalidraw
                  excalidrawAPI={setExcalidrawAPI}
                  initialData={initialData}
                  onChange={handleExcalidrawChange}
                  viewModeEnabled={!isMyTurn || !isTurnActive}
                  detectScroll={false}
                  autoFocus={true}
                  UIOptions={excalidrawUIOptions}
                />
              </Suspense>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
